import React, { useCallback } from 'react'
import styled from 'styled-components/macro'
import { useDropzone } from 'react-dropzone'

import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'

import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import FileList from './FileList'
import { ONE_MB } from 'utils/constants'
import getPdfSizes from 'helpers/browser/getPdfSizes'

const UnderlinedText = styled.span`
  text-decoration: underline;
`

function FileAttachDropzone({
  files,
  onChange,
  error,
  disabled,
  display,
  setFieldError,
  name = 'file',
  onRemove,
  multiple = true,
  maxSize = 100 * ONE_MB,
}) {
  const isAllowedSize = (width, height) => {
    const TOLERANCE = 2 // points
    const sizes = [
      { w: 612, h: 792 }, // Letter 8.5 x 11 in
      { w: 612, h: 1008 }, // Legal 8.5 x 14 in
      { w: 792, h: 1224 }, // Ledger/Tabloid 11 x 17 in
    ]

    const matches = (w1, h1, w2, h2) =>
      Math.abs(w1 - w2) <= TOLERANCE && Math.abs(h1 - h2) <= TOLERANCE

    return sizes.some(
      ({ w, h }) => matches(width, height, w, h) || matches(width, height, h, w)
    )
  }

  const validatePdfPageSizes = async (file) => {
    try {
      const pageSizes = await getPdfSizes(file)
      if (!Array.isArray(pageSizes) || pageSizes.length === 0) return false

      return pageSizes.every(({ width, height }) =>
        isAllowedSize(width, height)
      )
    } catch (e) {
      return false
    }
  }

  const onDrop = useCallback(
    async (acceptedFiles, rejectedFiles) => {
      if (rejectedFiles.length > 0) {
        const rejectedFile = rejectedFiles[0]
        if (rejectedFile) setFieldError(name, rejectedFile.errors?.[0]?.message)
      }

      if (acceptedFiles.length > 0) {
        const results = await Promise.all(
          acceptedFiles.map(async (file) => ({
            file,
            valid: await validatePdfPageSizes(file),
          }))
        )

        const validFiles = results.filter((r) => r.valid).map((r) => r.file)
        const invalidFiles = results
          .filter((r) => !r.valid)
          .map((r) => r.file.name)

        if (invalidFiles.length > 0) {
          setFieldError(
            name,
            `The following file(s) have non-standard page sizes. Allowed sizes are Letter (8.5x11), Legal (8.5x14), or Ledger (11x17): ${invalidFiles.join(
              ', '
            )}`
          )
        }

        if (validFiles.length > 0) {
          onChange(validFiles)
        }
      }
    },
    [onChange, setFieldError, name]
  )

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: 'application/pdf',
    maxSize,
    multiple,
    maxFiles: 3,
  })

  return (
    <>
      <Box component="label">{display}</Box>
      <Box
        component="div"
        {...getRootProps()}
        sx={{
          width: '100%',
          border: '1px solid',
          borderColor: error ? 'error.main' : 'lightgray.main',
          bgcolor: 'lightgray.light',
          borderRadius: '4px',
          display: 'flex',
          userSelect: 'none',
          cursor: 'pointer',
          flexDirection: 'column',
          py: !files?.length ? 12 : 0,
        }}
      >
        {!disabled && <Box component="input" {...getInputProps()} />}
        {!files?.length ? (
          <Box sx={{ width: '50%', m: 'auto', textAlign: 'center' }}>
            <Box>
              <CloudUploadIcon sx={{ color: 'darkgray.main' }} />
              <Typography color="darkgray.main">
                Drag and drop {multiple ? 'pdfs' : 'pdf'} here or{' '}
                <UnderlinedText>browse files</UnderlinedText>
              </Typography>
            </Box>
          </Box>
        ) : (
          <FileList files={files} onRemove={onRemove} />
        )}
      </Box>
      {error && (
        <Typography color="error.main" sx={{ mt: '1px' }}>
          {error}
        </Typography>
      )}
    </>
  )
}

export default FileAttachDropzone
